import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const toDMY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const rowKey = (m) => `${m.anno}-${m.codice_movimento}`;

const inp = { padding: "6px 8px", border: "1px solid #cfd8dc", borderRadius: 3, fontSize: 13 };
const btn = { padding: "7px 14px", border: "none", borderRadius: 3, cursor: "pointer", fontSize: 13, fontWeight: 600 };

export default function RiconciliazioneIncassiPage() {
  const { annoCorrente } = useAuth();
  const [filtri, setFiltri] = useState({ anno: "", alunno: "", classe: "", scad_da: "", scad_a: "", solo_non_pagati: true });
  const [risultati, setRisultati] = useState([]);
  const [meta, setMeta] = useState({ count: 0, totale: 0, troncato: false });
  const [loading, setLoading] = useState(false);
  const [selezione, setSelezione] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ric_selezione") || "{}") || {}; } catch (_e) { return {}; }
  }); // key -> movimento (persistita: resta finché non si generano i movimenti)
  const [importiPagati, setImportiPagati] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ric_importi_pagati") || "{}") || {}; } catch (_e) { return {}; }
  }); // key -> importo pagato (parziale)
  const [modalitaList, setModalitaList] = useState([]);
  const [dataPag, setDataPag] = useState("");
  const [modPag, setModPag] = useState("");
  const [saving, setSaving] = useState(false);
  const [esiti, setEsiti] = useState(null);
  const [msg, setMsg] = useState(null); // {tipo, testo}
  const [espanso, setEspanso] = useState(false); // finestra "Selezionati" a schermo intero
  const [spuntati, setSpuntati] = useState({}); // key -> true: spunta di verifica manuale (popup + stampa)

  useEffect(() => { api.get("/riconciliazione/modalita").then(r => setModalitaList(r.data.modalita || [])).catch(() => {}); }, []);

  // Persistenza selezione: resta anche uscendo dalla schermata, si svuota solo dopo aver generato i movimenti
  useEffect(() => {
    try { localStorage.setItem("ric_selezione", JSON.stringify(selezione)); } catch (_e) { /* noop */ }
  }, [selezione]);
  useEffect(() => {
    try { localStorage.setItem("ric_importi_pagati", JSON.stringify(importiPagati)); } catch (_e) { /* noop */ }
  }, [importiPagati]);

  const cerca = useCallback(async () => {
    setLoading(true); setMsg(null);
    try {
      const params = { solo_non_pagati: filtri.solo_non_pagati ? 1 : 0 };
      if (filtri.anno !== "" && filtri.anno != null) params.anno = filtri.anno;
      if (filtri.alunno.trim()) params.alunno = filtri.alunno.trim();
      if (filtri.classe.trim()) params.classe = filtri.classe.trim();
      if (filtri.scad_da) params.scad_da = toDMY(filtri.scad_da);
      if (filtri.scad_a) params.scad_a = toDMY(filtri.scad_a);
      const r = await api.get("/riconciliazione/movimenti", { params });
      setRisultati(r.data.movimenti || []);
      setMeta({ count: r.data.count || 0, totale: r.data.totale || 0, troncato: !!r.data.troncato });
    } catch (e) {
      setMsg({ tipo: "err", testo: e.response?.data?.detail || "Errore nella ricerca" });
    } finally { setLoading(false); }
  }, [filtri]);

  const toggleSel = (m) => {
    const k = rowKey(m);
    setSelezione(s => { const n = { ...s }; if (n[k]) delete n[k]; else n[k] = m; return n; });
  };
  const selTutti = () => setSelezione(s => { const n = { ...s }; risultati.forEach(m => { n[rowKey(m)] = m; }); return n; });
  const deselTutti = () => setSelezione(s => { const n = { ...s }; risultati.forEach(m => { delete n[rowKey(m)]; }); return n; });
  const rimuoviSel = (k) => { setSelezione(s => { const n = { ...s }; delete n[k]; return n; }); setImportiPagati(p => { const n = { ...p }; delete n[k]; return n; }); };
  const svuota = () => { setSelezione({}); setImportiPagati({}); setSpuntati({}); };
  const toggleSpunta = (k) => setSpuntati(s => { const n = { ...s }; if (n[k]) delete n[k]; else n[k] = true; return n; });
  const spuntaNessuno = () => setSpuntati({});

  // Stampa (A4) dell'elenco dei selezionati: Alunno, Causale, Anno, Scadenza, Importo, Importo pagato + totale
  const stampaSelezionati = () => {
    if (!selArr.length) return;
    const oggi = new Date().toLocaleDateString("it-IT");
    const righe = selArr.map((m, i) => {
      const parz = isParziale(m);
      const pagato = getPagato(m);
      const box = spuntati[rowKey(m)] ? "☑" : "☐";
      const pagCell = parz
        ? `${eur(pagato)} <span style="color:#90a4ae;font-size:10px">di ${eur(m.importo)}</span>`
        : eur(pagato);
      return `<tr>
        <td class="c box">${box}</td>
        <td class="c">${i + 1}</td>
        <td>${(m.alunno || "").toString().replace(/</g, "&lt;")} <span class="cod">#${m.codice_alunno || ""}</span></td>
        <td>${(m.causale || "").toString().replace(/</g, "&lt;")}${m.descrizione ? ` — ${(m.descrizione || "").toString().replace(/</g, "&lt;")}` : ""}</td>
        <td class="c">${m.anno_competenza || m.anno || ""}</td>
        <td class="c">${m.data_scadenza || ""}</td>
        <td class="r">${eur(m.importo)}</td>
        <td class="r"><b>${pagCell}</b></td>
      </tr>`;
    }).join("");
    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Bonifici — Selezionati</title>
      <style>
        @page{size:A4;margin:14mm}
        *{font-family:Arial,Helvetica,sans-serif;box-sizing:border-box}
        body{margin:0;color:#263238}
        h1{font-size:18px;color:#ad1457;margin:0 0 2px}
        .sub{font-size:11px;color:#607d8b;margin:0 0 10px}
        .tot{display:inline-block;background:#c62828;color:#fff;border-radius:6px;padding:6px 14px;font-size:14px;font-weight:800;margin-bottom:12px}
        .tot small{font-weight:400;opacity:.85;font-size:11px}
        table{width:100%;border-collapse:collapse;font-size:11px}
        th{background:#37474f;color:#fff;padding:6px 5px;text-align:left}
        td{padding:5px;border-bottom:1px solid #eceff1}
        th.c,td.c{text-align:center} th.r,td.r{text-align:right}
        td.box{font-size:15px;line-height:1}
        .cod{color:#b0bec5;font-weight:400}
        tfoot td{border-top:2px solid #37474f;font-weight:800;font-size:12px;padding-top:8px}
      </style></head><body>
      <h1>💶 Gestione Bonifici — Selezionati</h1>
      <p class="sub">Stampato il ${oggi} · ${selArr.length} movimenti${nParziali > 0 ? ` (${nParziali} parziali)` : ""}</p>
      <div class="tot">Totale da versare: ${eur(totaleSel)} <small>· ${selArr.length} movimenti</small></div>
      <table>
        <thead><tr>
          <th class="c">☑</th><th class="c">#</th><th>Alunno</th><th>Causale / Descrizione</th>
          <th class="c">Anno comp.</th><th class="c">Scadenza</th>
          <th class="r">Importo</th><th class="r">Importo pagato</th>
        </tr></thead>
        <tbody>${righe}</tbody>
        <tfoot><tr><td colspan="7" class="r">TOTALE</td><td class="r">${eur(totaleSel)}</td></tr></tfoot>
      </table>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { setMsg({ tipo: "err", testo: "Consenti i popup per stampare l'elenco." }); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (_e) { /* noop */ } }, 350);
  };

  const getPagato = (m) => {
    const v = importiPagati[rowKey(m)];
    return (v !== undefined && v !== "" && !isNaN(Number(v)) && Number(v) > 0) ? Number(v) : Number(m.importo || 0);
  };
  const isParziale = (m) => {
    const v = importiPagati[rowKey(m)];
    return v !== undefined && v !== "" && !isNaN(Number(v)) && Number(v) > 0 && Number(v) < Number(m.importo || 0);
  };
  const setPagato = (m, val) => {
    const k = rowKey(m);
    setImportiPagati(p => ({ ...p, [k]: val }));
    if (val !== "") setSelezione(s => (s[k] ? s : { ...s, [k]: m }));
  };
  // Freccia ➤ : conferma il pagamento TOTALE della riga (importo pagato = intero addebito) e seleziona
  const confermaTotale = (m) => {
    const k = rowKey(m);
    setImportiPagati(p => ({ ...p, [k]: Number(m.importo || 0).toFixed(2) }));
    setSelezione(s => (s[k] ? s : { ...s, [k]: m }));
  };

  const selArr = useMemo(() => Object.values(selezione), [selezione]);
  const totaleSel = useMemo(() => selArr.reduce((a, m) => a + getPagato(m), 0), [selArr, importiPagati]); // eslint-disable-line
  const nParziali = useMemo(() => selArr.filter(isParziale).length, [selArr, importiPagati]); // eslint-disable-line
  const nSpuntati = useMemo(() => selArr.filter(m => spuntati[rowKey(m)]).length, [selArr, spuntati]);
  const spuntaTutti = () => setSpuntati(() => { const n = {}; selArr.forEach(m => { n[rowKey(m)] = true; }); return n; });

  const assegna = async () => {
    if (!dataPag) { setMsg({ tipo: "err", testo: "Inserisci la data di pagamento" }); return; }
    if (!modPag) { setMsg({ tipo: "err", testo: "Seleziona la modalità di pagamento" }); return; }
    if (!selArr.length) { setMsg({ tipo: "err", testo: "Nessun movimento selezionato" }); return; }
    const modLabel = modalitaList.find(x => String(x.codice) === String(modPag))?.descrizione || modPag;
    const parzTxt = nParziali > 0 ? `\n• di cui ${nParziali} PARZIALI (verrà creato l'addebito residuo in automatico)` : "";
    if (!window.confirm(`Confermi? Verrà scritto in Segremat Web:\n\n• ${selArr.length} movimenti${parzTxt}\n• Data pagamento: ${toDMY(dataPag)}\n• Modalità: ${modLabel}\n• Totale incassato: ${eur(totaleSel)}\n\nSubito dopo, gli addebiti verranno riletti da Segremat per allineare l'app.`)) return;
    setSaving(true); setMsg(null); setEsiti(null);
    try {
      const body = {
        data_pagamento: toDMY(dataPag),
        mod_pagamento: parseInt(modPag, 10),
        movimenti: selArr.map(m => {
          const mv = { codice_alunno: m.codice_alunno, codice_movimento: m.codice_movimento, anno: m.anno };
          if (isParziale(m)) { mv.importo_parziale = getPagato(m); mv.importo = Number(m.importo || 0); }
          return mv;
        }),
      };
      const r = await api.post("/riconciliazione/paga", body);
      const res = r.data;
      setEsiti(res);
      // Rimuovi i riusciti da selezione
      const okKeys = new Set(res.risultati.filter(x => x.ok).map(x => `${x.anno}-${x.codice_movimento}`));
      setSelezione(s => { const n = { ...s }; Object.keys(n).forEach(k => { if (okKeys.has(k)) delete n[k]; }); return n; });
      setImportiPagati(p => { const n = { ...p }; Object.keys(n).forEach(k => { if (okKeys.has(k)) delete n[k]; }); return n; });
      // Ricarica la lista (il backend ha già risincronizzato da Segremat gli alunni pagati → allineati)
      try {
        const rr = await api.get("/riconciliazione/movimenti", { params: buildParams() });
        setRisultati(rr.data.movimenti || []);
        setMeta({ count: rr.data.count || 0, totale: rr.data.totale || 0, troncato: !!rr.data.troncato });
      } catch { /* lista non ricaricata */ }
      const sinc = res.risync && res.risync.totale != null ? ` · risincronizzati ${res.risync.totale} addebiti da Segremat` : "";
      setMsg({ tipo: res.n_err ? "warn" : "ok", testo: `Assegnati ${res.n_ok} pagamenti${res.n_err ? `, ${res.n_err} in errore` : ""}${sinc}.` });
    } catch (e) {
      setMsg({ tipo: "err", testo: e.response?.data?.detail || "Errore durante l'assegnazione" });
    } finally { setSaving(false); }
  };

  const buildParams = () => {
    const params = { solo_non_pagati: filtri.solo_non_pagati ? 1 : 0 };
    if (filtri.anno !== "" && filtri.anno != null) params.anno = filtri.anno;
    if (filtri.alunno.trim()) params.alunno = filtri.alunno.trim();
    if (filtri.classe.trim()) params.classe = filtri.classe.trim();
    if (filtri.scad_da) params.scad_da = toDMY(filtri.scad_da);
    if (filtri.scad_a) params.scad_a = toDMY(filtri.scad_a);
    return params;
  };

  const aggiornaAlunni = async () => {
    const codici = Array.from(new Set([...risultati.map(m => m.codice_alunno), ...selArr.map(m => m.codice_alunno)])).filter(c => c != null);
    if (!codici.length) { setMsg({ tipo: "err", testo: "Cerca prima un alunno, poi usa questo pulsante per aggiornarne gli addebiti." }); return; }
    setSaving(true); setMsg(null);
    try {
      const rs = await api.post("/riconciliazione/sync-alunno", { codici });
      const r = await api.get("/riconciliazione/movimenti", { params: buildParams() });
      const nuovi = r.data.movimenti || [];
      setRisultati(nuovi);
      setMeta({ count: r.data.count || 0, totale: r.data.totale || 0, troncato: !!r.data.troncato });
      // Preserva le spunte: aggiorna i selezionati con i dati freschi (importi ecc.), mantenendo la selezione
      const fresh = {}; nuovi.forEach(m => { fresh[rowKey(m)] = m; });
      setSelezione(s => { const n = {}; Object.keys(s).forEach(k => { n[k] = fresh[k] || s[k]; }); return n; });
      setMsg({ tipo: "ok", testo: `Addebiti aggiornati per ${codici.length} alunn${codici.length === 1 ? "o" : "i"} (${rs.data.totale} movimenti). Spunte mantenute.` });
    } catch (e) {
      setMsg({ tipo: "err", testo: e.response?.data?.detail || "Errore aggiornamento alunno" });
    } finally { setSaving(false); }
  };

  const msgStyle = { ok: { background: "#e8f5e9", color: "#1b5e20", border: "1px solid #a5d6a7" }, warn: { background: "#fff8e1", color: "#e65100", border: "1px solid #ffcc80" }, err: { background: "#ffebee", color: "#b71c1c", border: "1px solid #ef9a9a" } };

  return (
    <div data-testid="riconciliazione-page" style={{ padding: 16, display: "grid", gridTemplateColumns: "1fr 380px", gap: 16, alignItems: "start" }}>
      <style>{`input.ric-pagato.selezionato::placeholder{color:#ffd54f;opacity:.75}`}</style>
      {/* Colonna sinistra: ricerca + risultati */}
      <div>
        <h2 style={{ margin: "0 0 4px", fontSize: 20, color: "#ad1457" }}>💶 Gestione Bonifici</h2>
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "#607d8b" }}>Cerca gli addebiti, selezionali (anche in ricerche successive) fino a raggiungere il totale incassato del giorno, poi assegna data e modalità di pagamento — scrivendo direttamente in Segremat Web. Per un <b>incasso parziale</b>, scrivi l'importo pagato nella colonna gialla: l'addebito verrà messo a quella cifra e il residuo creato automaticamente. Usa la <b>freccia ➤</b> a sinistra dell'importo pagato per confermare al volo il pagamento <b>totale</b> di una riga.</p>

        <div style={{ background: "#f5f5f5", border: "1px solid #e0e0e0", borderRadius: 6, padding: 12, marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
          <label style={{ display: "flex", flexDirection: "column", fontSize: 11, fontWeight: 600, color: "#455a64", gap: 3 }}>Anno comp.
            <input data-testid="ric-anno" type="number" style={{ ...inp, width: 90 }} value={filtri.anno} placeholder="tutti" title="Vuoto = tutti gli anni" onChange={e => setFiltri(f => ({ ...f, anno: e.target.value ? parseInt(e.target.value, 10) : "" }))} />
          </label>
          <label style={{ display: "flex", flexDirection: "column", fontSize: 11, fontWeight: 600, color: "#455a64", gap: 3, flex: 1, minWidth: 160 }}>Alunno (cognome/nome o codice)
            <input data-testid="ric-alunno" style={inp} value={filtri.alunno} placeholder="es. Rossi Mario / 18904" onChange={e => setFiltri(f => ({ ...f, alunno: e.target.value }))} onKeyDown={e => e.key === "Enter" && cerca()} />
          </label>
          <label style={{ display: "flex", flexDirection: "column", fontSize: 11, fontWeight: 600, color: "#455a64", gap: 3, minWidth: 120 }}>Classe
            <input data-testid="ric-classe" style={inp} value={filtri.classe} placeholder="es. 1A" onChange={e => setFiltri(f => ({ ...f, classe: e.target.value }))} onKeyDown={e => e.key === "Enter" && cerca()} />
          </label>
          <label style={{ display: "flex", flexDirection: "column", fontSize: 11, fontWeight: 600, color: "#455a64", gap: 3 }}>Scadenza da
            <input data-testid="ric-scad-da" type="date" style={inp} value={filtri.scad_da} onChange={e => setFiltri(f => ({ ...f, scad_da: e.target.value }))} />
          </label>
          <label style={{ display: "flex", flexDirection: "column", fontSize: 11, fontWeight: 600, color: "#455a64", gap: 3 }}>a
            <input data-testid="ric-scad-a" type="date" style={inp} value={filtri.scad_a} onChange={e => setFiltri(f => ({ ...f, scad_a: e.target.value }))} />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#455a64" }}>
            <input data-testid="ric-solo-nonpagati" type="checkbox" checked={filtri.solo_non_pagati} onChange={e => setFiltri(f => ({ ...f, solo_non_pagati: e.target.checked }))} /> solo non pagati
          </label>
          <button data-testid="ric-cerca-btn" onClick={cerca} disabled={loading} style={{ ...btn, background: "#1565c0", color: "white" }}>{loading ? "Cerco…" : "🔍 Cerca"}</button>
          <button data-testid="ric-aggiorna-alunno-btn" onClick={aggiornaAlunni} disabled={saving || !risultati.length} title="Ri-sincronizza da Segremat solo gli addebiti degli alunni in elenco, mantenendo le spunte già fatte" style={{ ...btn, background: "#00695c", color: "white" }}>{saving ? "Aggiorno…" : "🔄 Aggiorna alunno"}</button>
        </div>

        {msg && <div data-testid="ric-msg" style={{ ...msgStyle[msg.tipo], padding: "8px 12px", borderRadius: 4, fontSize: 13, marginBottom: 10 }}>{msg.testo}</div>}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, fontSize: 12, color: "#546e7a" }}>
          <span data-testid="ric-count">Trovati <b>{meta.count}</b> movimenti · totale {eur(meta.totale)}{meta.troncato ? " (mostrati i primi 500)" : ""}</span>
          <span>
            <button data-testid="ric-sel-tutti" onClick={selTutti} disabled={!risultati.length} style={{ ...btn, background: "#eceff1", color: "#37474f", padding: "4px 10px", marginRight: 6 }}>Seleziona tutti</button>
            <button data-testid="ric-desel-tutti" onClick={deselTutti} disabled={!risultati.length} style={{ ...btn, background: "#eceff1", color: "#37474f", padding: "4px 10px" }}>Deseleziona</button>
          </span>
        </div>

        <div style={{ border: "1px solid #e0e0e0", borderRadius: 6, overflow: "auto", maxHeight: "62vh" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead style={{ position: "sticky", top: 0, background: "#37474f", color: "white", zIndex: 1 }}>
              <tr>
                <th style={{ padding: 6, width: 30 }}></th>
                <th style={{ padding: 6, textAlign: "left" }}>Alunno</th>
                <th style={{ padding: 6, textAlign: "left" }}>Descrizione</th>
                <th style={{ padding: 6, textAlign: "left" }}>Causale</th>
                <th style={{ padding: 6, textAlign: "center" }}>Anno comp.</th>
                <th style={{ padding: 6, textAlign: "center" }}>Scadenza</th>
                <th style={{ padding: 6, textAlign: "center" }}>Data pag.</th>
                <th style={{ padding: 6, textAlign: "left" }}>Modalità</th>
                <th style={{ padding: 6, textAlign: "right" }}>Importo</th>
                <th style={{ padding: 6, textAlign: "right", background: "#f9a825", color: "#3e2723" }}>Importo pagato</th>
              </tr>
            </thead>
            <tbody>
              {risultati.length === 0 ? (
                <tr><td colSpan={10} style={{ padding: 20, textAlign: "center", color: "#90a4ae" }}>Nessun risultato. Usa i filtri e premi Cerca.</td></tr>
              ) : risultati.map(m => {
                const k = rowKey(m); const sel = !!selezione[k]; const parz = isParziale(m);
                return (
                  <tr key={k} data-testid={`ric-row-${m.codice_movimento}`} onClick={() => toggleSel(m)}
                    style={{ borderTop: "1px solid #eceff1", cursor: "pointer", background: parz ? "#fff3e0" : (sel ? "#e3f2fd" : "white") }}>
                    <td style={{ padding: 6, textAlign: "center" }}><input data-testid={`ric-check-${m.codice_movimento}`} type="checkbox" checked={sel} onChange={() => toggleSel(m)} onClick={e => e.stopPropagation()} /></td>
                    <td style={{ padding: 6 }}>{m.alunno} <span style={{ color: "#b0bec5" }}>#{m.codice_alunno}</span></td>
                    <td style={{ padding: 6 }}>{m.descrizione}</td>
                    <td style={{ padding: 6 }}>{m.causale}</td>
                    <td style={{ padding: 6, textAlign: "center" }}><span style={{ background: "#eceff1", color: "#37474f", fontWeight: 700, borderRadius: 3, padding: "0 5px", fontSize: 11 }}>{m.anno_competenza || m.anno}</span></td>
                    <td style={{ padding: 6, textAlign: "center" }}>{m.data_scadenza}</td>
                    <td style={{ padding: 6, textAlign: "center", color: m.data_pagamento ? "#1b5e20" : "#b0bec5", fontWeight: m.data_pagamento ? 600 : 400 }}>{m.data_pagamento || "—"}</td>
                    <td style={{ padding: 6, color: m.modalita ? "#37474f" : "#b0bec5" }}>{m.modalita || "—"}</td>
                    <td style={{ padding: 6, textAlign: "right", fontWeight: 600 }}>{eur(m.importo)}</td>
                    <td style={{ padding: 4, textAlign: "right" }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                        <button data-testid={`ric-conferma-tot-${m.codice_movimento}`} onClick={() => confermaTotale(m)}
                          title="Conferma il pagamento TOTALE di questa riga (imposta l'importo pagato pari all'intero addebito)"
                          style={{ border: "none", background: sel && !parz ? "#000" : "#f9a825", color: sel && !parz ? "#ffeb3b" : "#3e2723", borderRadius: 3, cursor: "pointer", fontWeight: 800, fontSize: 15, lineHeight: 1, padding: "3px 7px" }}>➤</button>
                        <input data-testid={`ric-pagato-${m.codice_movimento}`} type="number" step="0.01" min="0"
                          className={`ric-pagato${sel ? " selezionato" : ""}`}
                          value={importiPagati[k] ?? ""} placeholder={Number(m.importo || 0).toFixed(2)}
                          onChange={e => setPagato(m, e.target.value)}
                          title="Lascia vuoto = pagamento intero. Inserisci un importo minore per pagamento parziale (crea il residuo)."
                          style={{ width: 92, padding: "4px 6px", textAlign: "right", border: `2px solid ${parz ? "#ef6c00" : "#f9a825"}`, borderRadius: 3, background: sel ? "#000" : "#fffde7", color: sel ? "#ffeb3b" : "#3e2723", fontWeight: 700, fontSize: 12 }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Colonna destra: carrello selezione */}
      <div style={{ position: "sticky", top: 16, background: "white", border: "2px solid #c62828", borderRadius: 8, padding: 14, boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 16, color: "#c62828" }}>Selezionati</h3>
          <div style={{ display: "flex", gap: 6 }}>
            <button data-testid="ric-espandi" onClick={() => setEspanso(true)} disabled={!selArr.length} title="Apri i selezionati a schermo intero" style={{ ...btn, background: "#1565c0", color: "white", padding: "3px 8px", fontSize: 11 }}>⛶ Espandi</button>
            <button data-testid="ric-stampa-sel" onClick={stampaSelezionati} disabled={!selArr.length} title="Stampa l'elenco dei selezionati (A4)" style={{ ...btn, background: "#455a64", color: "white", padding: "3px 8px", fontSize: 11 }}>🖨 Stampa</button>
            <button data-testid="ric-svuota" onClick={svuota} disabled={!selArr.length} style={{ ...btn, background: "#eceff1", color: "#37474f", padding: "3px 8px", fontSize: 11 }}>Svuota</button>
          </div>
        </div>
        <div data-testid="ric-tot-sel" style={{ background: "#c62828", color: "white", borderRadius: 6, padding: "10px 12px", textAlign: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 11, opacity: 0.85 }}>{selArr.length} movimenti selezionati{nParziali > 0 ? ` · ${nParziali} parziali` : ""}</div>
          <div style={{ fontSize: 26, fontWeight: 800 }}>{eur(totaleSel)}</div>
        </div>

        <div style={{ maxHeight: 200, overflow: "auto", marginBottom: 10, border: "1px solid #f0f0f0", borderRadius: 4 }}>
          {selArr.length === 0 ? <div style={{ padding: 14, textAlign: "center", color: "#b0bec5", fontSize: 12 }}>Spunta gli addebiti dalla lista a sinistra</div>
            : selArr.map(m => (
              <div key={rowKey(m)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, padding: "5px 8px", borderBottom: "1px solid #f5f5f5", fontSize: 12 }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <span data-testid={`ric-sel-anncom-${m.codice_movimento}`} style={{ display: "inline-block", background: "#eceff1", color: "#37474f", fontWeight: 700, borderRadius: 3, padding: "0 5px", marginRight: 5, fontSize: 10 }}>{m.anno_competenza || m.anno}</span>
                  {m.alunno} · {m.causale}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {isParziale(m) ? <b style={{ color: "#e65100" }}>{eur(getPagato(m))} <span style={{ fontWeight: 400, color: "#90a4ae", fontSize: 10 }}>di {eur(m.importo)}</span></b> : <b>{eur(m.importo)}</b>}
                  <button data-testid={`ric-rimuovi-${m.codice_movimento}`} onClick={() => rimuoviSel(rowKey(m))} title="Rimuovi" style={{ ...btn, background: "transparent", color: "#e53935", padding: 0, fontSize: 14 }}>✕</button>
                </span>
              </div>
            ))}
        </div>

        <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#455a64", marginBottom: 3 }}>Data pagamento</label>
        <input data-testid="ric-data-pag" type="date" style={{ ...inp, width: "100%", marginBottom: 8, boxSizing: "border-box" }} value={dataPag} onChange={e => setDataPag(e.target.value)} />
        <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#455a64", marginBottom: 3 }}>Modalità di pagamento</label>
        <select data-testid="ric-mod-pag" style={{ ...inp, width: "100%", marginBottom: 12, boxSizing: "border-box" }} value={modPag} onChange={e => setModPag(e.target.value)}>
          <option value="">— scegli —</option>
          {modalitaList.map(m => <option key={m.codice} value={m.codice}>{m.descrizione}</option>)}
        </select>

        <button data-testid="ric-assegna-btn" onClick={assegna} disabled={saving || !selArr.length} style={{ ...btn, width: "100%", background: "#2e7d32", color: "white", padding: "10px", fontSize: 14, marginBottom: 8 }}>
          {saving ? "Scrittura + sincronizzazione…" : "✓ Assegna pagamento in Segremat"}
        </button>
        <div style={{ fontSize: 10, color: "#90a4ae", textAlign: "center", lineHeight: 1.4 }}>Dopo l'assegnazione gli addebiti vengono riletti da Segremat: le due gestioni restano allineate.</div>

        {esiti && (
          <div data-testid="ric-esiti" style={{ marginTop: 10, fontSize: 11, maxHeight: 160, overflow: "auto", border: "1px solid #eceff1", borderRadius: 4, padding: 6 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Esito: {esiti.n_ok} OK · {esiti.n_err} errori</div>
            {esiti.risultati.filter(r => !r.ok).map((r, i) => (
              <div key={i} style={{ color: "#b71c1c" }}>#{r.codice_movimento} (alu {r.codice_alunno}): {r.errore}</div>
            ))}
          </div>
        )}
      </div>

      {/* Finestra "Selezionati" a schermo intero */}
      {espanso && (
        <div data-testid="ric-espanso-overlay" onClick={() => setEspanso(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div data-testid="ric-espanso-modal" onClick={e => e.stopPropagation()}
            style={{ background: "white", borderRadius: 10, width: "min(900px, 96vw)", maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 12px 40px rgba(0,0,0,0.3)", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: "2px solid #c62828" }}>
              <h3 style={{ margin: 0, fontSize: 20, color: "#c62828" }}>💶 Selezionati</h3>
              <div style={{ display: "flex", gap: 8 }}>
                <button data-testid="ric-espanso-stampa" onClick={stampaSelezionati} disabled={!selArr.length} style={{ ...btn, background: "#455a64", color: "white" }}>🖨 Stampa</button>
                <button data-testid="ric-espanso-svuota" onClick={svuota} disabled={!selArr.length} style={{ ...btn, background: "#eceff1", color: "#37474f" }}>Svuota</button>
                <button data-testid="ric-espanso-chiudi" onClick={() => setEspanso(false)} style={{ ...btn, background: "#c62828", color: "white" }}>✕ Chiudi</button>
              </div>
            </div>
            <div style={{ padding: "12px 18px", borderBottom: "1px solid #f0f0f0" }}>
              <div style={{ background: "#c62828", color: "white", borderRadius: 6, padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, opacity: 0.9 }}>{selArr.length} movimenti selezionati{nParziali > 0 ? ` · ${nParziali} parziali` : ""}</span>
                <span style={{ fontSize: 28, fontWeight: 800 }}>{eur(totaleSel)}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, fontSize: 12, color: "#37474f" }}>
                <span data-testid="ric-espanso-spuntati-count" style={{ fontWeight: 700 }}>✅ Spuntati {nSpuntati}/{selArr.length}</span>
                <button data-testid="ric-espanso-spunta-tutti" onClick={spuntaTutti} disabled={!selArr.length} style={{ ...btn, background: "#2e7d32", color: "white", padding: "4px 10px", fontSize: 11 }}>Spunta tutti</button>
                <button data-testid="ric-espanso-spunta-nessuno" onClick={spuntaNessuno} disabled={!nSpuntati} style={{ ...btn, background: "#eceff1", color: "#37474f", padding: "4px 10px", fontSize: 11 }}>Azzera spunte</button>
                <span style={{ color: "#90a4ae", fontSize: 11 }}>· spunta le righe man mano che le verifichi (le spunte finiscono anche nella stampa)</span>
              </div>
            </div>
            <div style={{ overflow: "auto", flex: 1 }}>
              {selArr.length === 0 ? (
                <div style={{ padding: 30, textAlign: "center", color: "#b0bec5" }}>Nessun addebito selezionato</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead style={{ position: "sticky", top: 0, background: "#37474f", color: "white", zIndex: 1 }}>
                    <tr>
                      <th style={{ padding: 8, textAlign: "center", width: 40 }}>☑</th>
                      <th style={{ padding: 8, textAlign: "center", width: 36 }}>#</th>
                      <th style={{ padding: 8, textAlign: "left" }}>Alunno</th>
                      <th style={{ padding: 8, textAlign: "left" }}>Causale / Descrizione</th>
                      <th style={{ padding: 8, textAlign: "center" }}>Anno comp.</th>
                      <th style={{ padding: 8, textAlign: "center" }}>Scadenza</th>
                      <th style={{ padding: 8, textAlign: "right" }}>Importo</th>
                      <th style={{ padding: 8, textAlign: "right" }}>Importo pagato</th>
                      <th style={{ padding: 8, width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {selArr.map((m, i) => {
                      const parz = isParziale(m); const pagato = getPagato(m); const sp = !!spuntati[rowKey(m)];
                      return (
                        <tr key={rowKey(m)} data-testid={`ric-espanso-row-${m.codice_movimento}`} onClick={() => toggleSpunta(rowKey(m))}
                          style={{ borderTop: "1px solid #eceff1", cursor: "pointer", background: sp ? "#e8f5e9" : (parz ? "#fff3e0" : "white") }}>
                          <td style={{ padding: 8, textAlign: "center" }} onClick={e => e.stopPropagation()}>
                            <input data-testid={`ric-espanso-spunta-${m.codice_movimento}`} type="checkbox" checked={sp} onChange={() => toggleSpunta(rowKey(m))} style={{ width: 17, height: 17, cursor: "pointer" }} />
                          </td>
                          <td style={{ padding: 8, textAlign: "center", color: "#90a4ae" }}>{i + 1}</td>
                          <td style={{ padding: 8, textDecoration: sp ? "line-through" : "none", color: sp ? "#78909c" : "inherit" }}>{m.alunno} <span style={{ color: "#b0bec5" }}>#{m.codice_alunno}</span></td>
                          <td style={{ padding: 8, textDecoration: sp ? "line-through" : "none", color: sp ? "#78909c" : "inherit" }}>{m.causale}{m.descrizione ? ` — ${m.descrizione}` : ""}</td>
                          <td style={{ padding: 8, textAlign: "center" }}><span style={{ background: "#eceff1", color: "#37474f", fontWeight: 700, borderRadius: 3, padding: "0 5px", fontSize: 11 }}>{m.anno_competenza || m.anno}</span></td>
                          <td style={{ padding: 8, textAlign: "center" }}>{m.data_scadenza}</td>
                          <td style={{ padding: 8, textAlign: "right" }}>{eur(m.importo)}</td>
                          <td style={{ padding: 8, textAlign: "right", fontWeight: 700, color: parz ? "#e65100" : "#1b5e20" }}>
                            {parz ? <>{eur(pagato)} <span style={{ fontWeight: 400, color: "#90a4ae", fontSize: 11 }}>di {eur(m.importo)}</span></> : eur(pagato)}
                          </td>
                          <td style={{ padding: 8, textAlign: "center" }} onClick={e => e.stopPropagation()}>
                            <button data-testid={`ric-espanso-rimuovi-${m.codice_movimento}`} onClick={() => rimuoviSel(rowKey(m))} title="Rimuovi" style={{ ...btn, background: "transparent", color: "#e53935", padding: 0, fontSize: 16 }}>✕</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: "2px solid #37474f", fontWeight: 800 }}>
                      <td colSpan={7} style={{ padding: 10, textAlign: "right" }}>TOTALE</td>
                      <td style={{ padding: 10, textAlign: "right" }}>{eur(totaleSel)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
